import React, { SetStateAction, useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import { Pagination } from "@mui/material";
import { NeutralColor, SecondaryColor } from "node-library/dist/colors";
import { Severity, SystemAddOn } from "node-library/dist/constants";
import {
	SortOrder,
	TempFilePasswordManagementTableSortColumn,
} from "node-library/dist/paginations";
import {
	FrontendCompanyWithAdditionalInfomation,
	FrontendTempFilePasswordManagement,
	FrontendUserInfo,
} from "node-library/dist/portal";
import { useTranslation } from "react-i18next";
import { BlackButton } from "../../../common-components/button";
import {
	CustomSearchBar,
	RowDropdown,
} from "../../../common-components/inputs";
import { SortArrowIcon } from "../../../common-components/svgIcons";
import {
	RedErrorIcon,
	RedTrashIcon,
} from "../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../common-components/toast/successIcons";
import { getTempFilePasswordSortString } from "../../../helpers/tempFilePasswordHelper";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../../requestHelper/sendRequest";
import ViewOrderPageSkeletonUI from "../viewOrders/skeletonUI/orderHistoryPageSkeleton";
import FilePasswordDetailsContent from "./filePasswordDetailsContent";
import AddOrEditFilePasswordModal from "./modal/addOrEditFilePasswordModal";
import DeleteFilePasswordConfirmModal from "./modal/deleteFilePasswordConfirmModal";
import TempFilePasswordContent from "./tempFilePasswordContent";
import { doesCompanyHaveAddonsEnabled } from "node-library/dist/helpers/request_helper";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	user: FrontendUserInfo | undefined;
	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
}

export default function TempFilePasswordPage(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const setUnsavedChanges = props.setUnsavedChanges;

	const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

	const [filePasswords, setFilePasswords] = useState<
		Array<FrontendTempFilePasswordManagement>
	>([]);

	const [selectedFilePassword, setSelectedFilePassword] =
		useState<FrontendTempFilePasswordManagement>();

	const [loading, setLoading] = useState<boolean>(false);

	const [adminInAction, setAdminInAction] = useState<boolean>(false);
	const [searchText, setSearchText] = useState<string>("");

	//paginations
	const [totalPageCount, setTotalPageCount] = useState<number>(0);
	const [sortBy, setSortBy] =
		useState<TempFilePasswordManagementTableSortColumn>(
			TempFilePasswordManagementTableSortColumn.INSERTED
		);
	const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.DESC);
	const [currentPagination, setCurrentPagination] = useState<number>(1);

	const [addEditModalOpen, setAddEditModalOpen] = useState<boolean>(false);

	//confirm modals
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
	const [proceedAction, setProceedAction] = useState<() => void>(
		() => () => {}
	);

	function toggleSortOrder() {
		if (sortOrder === SortOrder.ASC) {
			setSortOrder(SortOrder.DESC);
		} else {
			setSortOrder(SortOrder.ASC);
		}
	}

	const updateFile = (updatedFile: FrontendTempFilePasswordManagement) => {
		setFilePasswords((prevFile) =>
			prevFile.map((file) =>
				file.id === updatedFile.id ? { ...file, ...updatedFile } : file
			)
		);
	};
	async function getFilePassword(searchText: string) {
		setLoading(true);
		try {
			const response = await sendGETRequest(
				"/file-password/get-company-file-password",
				{
					searchText,
					sortBy,
					sortOrder,
					pageNumber: currentPagination,
				}
			);

			const files: Array<FrontendTempFilePasswordManagement> =
				response?.data?.entries;
			const pageCount: number = response?.data?.totalPages;
			setFilePasswords(files);
			setTotalPageCount(pageCount);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	async function createFile(file: FrontendTempFilePasswordManagement) {
		setAdminInAction(true);
		try {
			const response = await sendPOSTRequest(
				"/file-password/add-file-password",
				{
					name: file.name,
					password: file.password,
				}
			);

			openSnackbar(
				translation("EntryCreated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />
			);
			goBackToViewFilesPage(true);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setAdminInAction(false);
		}
	}

	async function editFile(file: FrontendTempFilePasswordManagement) {
		setAdminInAction(true);
		try {
			const response = await sendPOSTRequest(
				"/file-password/update-file-password",
				{
					id: file.id,
					name: file.name,
					password: file.password,
				}
			);

			openSnackbar(
				translation("EntryUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />
			);
			setAddEditModalOpen(false);
			updateFile(file);
			setSelectedFilePassword(file);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setAdminInAction(false);
		}
	}

	async function deleteFile(fileId: string) {
		setAdminInAction(true);
		try {
			const response = await sendPOSTRequest(
				"/file-password/delete-file-password",
				{
					id: fileId,
				}
			);

			openSnackbar(
				translation("EntryDeleted"),
				Severity.ERROR,
				undefined,
				<RedTrashIcon />
			);
			setDeleteConfirmOpen(false);

			goBackToViewFilesPage(true);
			setSelectedFilePassword(undefined);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setAdminInAction(false);
		}
	}

	const sortOptionSelection: React.ReactNode = (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: "5px",
				marginLeft: "auto",
			}}
		>
			<div
				onClick={() => {
					toggleSortOrder();
				}}
				style={{ cursor: "pointer" }}
			>
				<SortArrowIcon sortOrder={sortOrder} />
			</div>
			<RowDropdown
				id="order-sort-order-dropdown"
				className="sort-order-dropdown"
				label={"a"}
				labelClassName="hide"
				options={Object.values(TempFilePasswordManagementTableSortColumn).map(
					(value) => {
						return {
							value: value,
							title: getTempFilePasswordSortString(value, translation),
						};
					}
				)}
				value={sortBy}
				setValue={setSortBy}
				noBorder
			></RowDropdown>
		</div>
	);

	useEffect(() => {
		getFilePassword(searchText);
	}, [currentPagination, sortBy, sortOrder]);

	function gotoFileDetailsPage(file: FrontendTempFilePasswordManagement) {
		setSelectedFilePassword(file);
		setCurrentPageIndex(1);
	}
	function goBackToViewFilesPage(reload: boolean) {
		setCurrentPageIndex(0);
		setSelectedFilePassword(undefined);
		if (reload) {
			getFilePassword(searchText);
		}
	}

	let currentComponent: JSX.Element = <></>;
	switch (currentPageIndex) {
		case 0:
			currentComponent = (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "20px",
						minHeight: "90vh",
						marginTop: "60px",
					}}
				>
					<div
						style={{
							flex: "1",
							display: "flex",
							flexDirection: "column",
							gap: "20px",
						}}
					>
						<div className="view-orders-page-header">
							<div className="typography-heading-01">
								{translation("FilePasswordManagement")}
							</div>
							<CustomSearchBar
								id="view-order-page-searchbar"
								searchText={searchText}
								setSearchText={setSearchText}
								placeholder={translation("SearchByFileName")}
								searchButtonText={translation("Search")}
								onSearch={() => {
									if (currentPagination !== 1) {
										setCurrentPageIndex(1);
									} else {
										getFilePassword(searchText);
									}
								}}
							></CustomSearchBar>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								flexWrap: "wrap",
								gap: "10px",
							}}
						>
							{sortOptionSelection}
						</div>
						<div style={{ display: "flex" }}>
							<BlackButton
								key="add-client-button"
								id="add-client-button"
								size="medium"
								children={
									<>
										<AddIcon></AddIcon>
										{translation("File")}
									</>
								}
								onClick={() => {
									setAddEditModalOpen(true);
								}}
							></BlackButton>
						</div>

						{loading ? (
							<ViewOrderPageSkeletonUI />
						) : (
							<TempFilePasswordContent
								filePasswords={filePasswords}
								goToViewFileDetails={gotoFileDetailsPage}
							/>
						)}
					</div>
					{!loading && filePasswords.length > 0 && (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
							}}
						>
							<Pagination
								count={totalPageCount}
								page={currentPagination}
								onChange={(e, value) => {
									setCurrentPagination(value);
								}}
								sx={{
									backgroundColor: "white",
									borderRadius: "30px",
									padding: "6px 16px",

									"& .MuiPaginationItem-root": {
										backgroundColor: "white",
										color: "black",
										border: "1px solid transparent", // Hide outline when not selected
										"&:hover": {
											backgroundColor: "#f5f5f5", // Change background on hover
										},
										borderRadius: "50%",
									},
									"& .Mui-selected": {
										backgroundColor: SecondaryColor.SECONDARY_50, // Change background for selected item
										border: `1px solid ${NeutralColor.NEUTRAL_GREY_950}`, // Change text color for selected item
									},
								}}
								variant="outlined"
								shape="rounded"
							/>
						</div>
					)}
				</div>
			);
			break;
		case 1:
			currentComponent = (
				<FilePasswordDetailsContent
					company={props.company}
					filePassword={
						selectedFilePassword as FrontendTempFilePasswordManagement
					}
					goBackToViewFilePage={goBackToViewFilesPage}
					adminInAction={adminInAction}
					setAddEditModalOpen={setAddEditModalOpen}
					setDeleteConfirmOpen={setDeleteConfirmOpen}
					deleteFile={deleteFile}
					setProceedAction={setProceedAction}
				></FilePasswordDetailsContent>
			);
			break;
	}

	return doesCompanyHaveAddonsEnabled(props.company, [
		SystemAddOn.TEMP_PASSWORD_MANAGEMENT,
	]) ? (
		<div style={{ margin: "0 auto" }}>
			{currentComponent}

			{
				<AddOrEditFilePasswordModal
					company={props.company}
					addOrEditFilePassword={selectedFilePassword ? editFile : createFile}
					open={addEditModalOpen}
					setOpen={setAddEditModalOpen}
					filePassword={selectedFilePassword}
				/>
			}
			{
				<DeleteFilePasswordConfirmModal
					open={deleteConfirmOpen}
					setOpen={setDeleteConfirmOpen}
					proceedAction={proceedAction}
				></DeleteFilePasswordConfirmModal>
			}
		</div>
	) : (
		<></>
	);
}
